import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api/client.js';
import clsx from 'clsx';

/**
 * Customer 360 / Account Scorecard.
 *
 * Top-line "over-under" answer for an account: are we ahead or behind on
 * trailing 12 months vs the prior 12 months, broken into parts + service +
 * combined, plus fleet age and comeback rate from the unified so_history.
 *
 * Lives at the top of CustomerDetail under the contact strip. Self-contained
 * — gracefully renders nothing if the scorecard endpoint 404s (e.g. for a
 * brand-new customer not yet in any sales history table).
 */
export default function CustomerScorecard({ customerId }) {
  const [showFleet, setShowFleet] = useState(false);
  const q = useQuery({
    queryKey: ['customer-scorecard', customerId],
    queryFn: () => api.get(`/customers/${customerId}/scorecard`).then(r => r.data.data),
    retry: false,
  });

  if (q.isLoading) return <div className="text-muted text-sm">Loading scorecard…</div>;
  if (q.isError) return null; // silent — endpoint may not have data yet
  const s = q.data;
  if (!s) return null;

  return (
    <section className="card p-4 space-y-4">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="section-eyebrow">Account scorecard</div>
          <Verdict verdict={s.verdict} delta={s.revenue.total} />
          {s.customer.group_size > 1 && (
            <div className="text-xs text-muted mt-1">
              unified across {s.customer.group_size} customer rows (Karmak + Excede)
            </div>
          )}
        </div>
        <div className="text-xs text-muted">vs prior 12 months</div>
      </div>
      <div className="rule-crimson" />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <RevenueCard label="Parts"   r={s.revenue.parts} />
        <RevenueCard label="Service" r={s.revenue.service} />
        {s.revenue.trailer && <RevenueCard label="Trailer" r={s.revenue.trailer} />}
        <RevenueCard label="Combined" r={s.revenue.total} bold />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <FleetCard fleet={s.fleet} onToggle={() => setShowFleet(v => !v)} expanded={showFleet} />
        <ActivityCard activity={s.activity} />
      </div>

      {showFleet && s.fleet.units.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-muted uppercase tracking-wider">
              <tr>
                <th className="py-1 pr-3">Year</th>
                <th className="py-1 pr-3">Make</th>
                <th className="py-1 pr-3">Model</th>
                <th className="py-1 pr-3">VIN</th>
                <th className="py-1 pr-3">In service</th>
                <th className="py-1 pr-3 text-right">Odom</th>
              </tr>
            </thead>
            <tbody>
              {s.fleet.units.map(u => (
                <tr key={u.unt_id} className="border-t border-panel">
                  <td className="py-1 pr-3">{u.model_year || '—'}</td>
                  <td className="py-1 pr-3">{u.make || '—'}</td>
                  <td className="py-1 pr-3">{u.model || '—'}</td>
                  <td className="py-1 pr-3 font-mono text-[11px]">{u.vin || '—'}</td>
                  <td className="py-1 pr-3">{u.date_in_service ? new Date(u.date_in_service).toLocaleDateString() : '—'}</td>
                  <td className="py-1 pr-3 text-right">{u.odom ? u.odom.toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Verdict({ verdict, delta }) {
  const map = {
    over:   { label: 'AHEAD',   color: 'text-emerald-700', sign: '+' },
    under:  { label: 'BEHIND',  color: 'text-crimson',     sign: ''  },
    flat:   { label: 'FLAT',    color: 'text-navy',        sign: ''  },
    new:    { label: 'NEW',     color: 'text-emerald-700', sign: '+' },
    silent: { label: 'SILENT',  color: 'text-crimson',     sign: ''  },
  };
  const v = map[verdict] || map.flat;
  const abs = Math.abs(delta.abs_delta);
  const pct = delta.pct_delta;
  return (
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className={clsx('text-2xl font-bold', v.color)}>{v.label}</span>
      {verdict !== 'flat' && verdict !== 'new' && verdict !== 'silent' && (
        <>
          <span className={clsx('text-xl font-semibold', v.color)}>
            {v.sign}{fmtMoney(abs)}
          </span>
          {pct != null && (
            <span className={clsx('text-sm', v.color)}>
              ({pct > 0 ? '+' : ''}{pct.toFixed(0)}%)
            </span>
          )}
        </>
      )}
      {verdict === 'new' && <span className="text-sm text-muted">no prior-year baseline</span>}
      {verdict === 'silent' && <span className="text-sm text-muted">no activity in last 12mo</span>}
    </div>
  );
}

function RevenueCard({ label, r, bold }) {
  const delta = r.abs_delta;
  const pct = r.pct_delta;
  const positive = delta > 0;
  const negative = delta < 0;
  return (
    <div className={clsx('bg-panel rounded p-3', bold && 'ring-1 ring-navy')}>
      <div className="text-[10px] uppercase tracking-wider text-muted">{label} — last 12mo</div>
      <div className={clsx('text-xl', bold ? 'font-bold' : 'font-semibold')}>{fmtMoney(r.t12)}</div>
      <div className="text-xs mt-1">
        prev: <span className="text-muted">{fmtMoney(r.p12)}</span>
        {Math.abs(delta) > 0 && (
          <span className={clsx('ml-2 font-medium',
            positive && 'text-emerald-700', negative && 'text-crimson')}>
            {positive ? '▲' : '▼'} {fmtMoney(Math.abs(delta))}
            {pct != null && ` (${pct > 0 ? '+' : ''}${pct.toFixed(0)}%)`}
          </span>
        )}
      </div>
    </div>
  );
}

function FleetCard({ fleet, onToggle, expanded }) {
  if (!fleet.total) {
    return (
      <div className="bg-panel rounded p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted">Fleet</div>
        <div className="text-sm text-muted mt-1">No units on file (Procede VHVIN).</div>
      </div>
    );
  }
  return (
    <div className="bg-panel rounded p-3">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted">Fleet</div>
          <div className="text-xl font-semibold">{fleet.owned} units</div>
        </div>
        {fleet.units.length > 0 && (
          <button onClick={onToggle} className="text-xs text-crimson hover:underline">
            {expanded ? 'hide list' : `view all ${fleet.units.length}`}
          </button>
        )}
      </div>
      <div className="text-xs mt-2 space-y-0.5">
        {fleet.avg_age_years != null && (
          <div>avg age: <span className="font-medium">{fleet.avg_age_years}y</span></div>
        )}
        {fleet.pct_over_10y != null && (
          <div>
            over 10y old: <span className={clsx('font-medium', fleet.pct_over_10y >= 50 && 'text-crimson')}>
              {fleet.over_10y} ({fleet.pct_over_10y.toFixed(0)}%)
            </span>
          </div>
        )}
        {fleet.oldest_year && (
          <div className="text-muted">range: {fleet.oldest_year} – {fleet.newest_year}</div>
        )}
      </div>
    </div>
  );
}

function ActivityCard({ activity }) {
  const last = activity.last_invoice_at || activity.last_service_at || activity.last_parts_at;
  const lastDays = last ? Math.floor((Date.now() - new Date(last).getTime()) / 86400000) : null;
  return (
    <div className="bg-panel rounded p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted">Service activity</div>
      <div className="text-xl font-semibold">{activity.jobs_t12} jobs</div>
      <div className="text-xs mt-2 space-y-0.5">
        {activity.jobs_p12 > 0 && (
          <div>prev 12mo: <span className="text-muted">{activity.jobs_p12} jobs</span></div>
        )}
        {activity.comeback_rate_pct != null && (
          <div>
            comeback rate:
            <span className={clsx('ml-1 font-medium',
              activity.comeback_rate_pct > 5 && 'text-crimson',
              activity.comeback_rate_pct === 0 && 'text-emerald-700')}>
              {activity.comeback_rate_pct.toFixed(1)}%
            </span>
            <span className="text-muted"> ({activity.comebacks_t12}/{activity.jobs_t12})</span>
          </div>
        )}
        {lastDays != null && (
          <div className={clsx(lastDays > 90 && 'text-crimson')}>
            last visit: {lastDays}d ago
          </div>
        )}
      </div>
    </div>
  );
}

function fmtMoney(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}
